import { useMemo } from 'react';
import { useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';

/**
 * Status item from referential with color from database
 */
export interface StatusItem {
  id: string;
  code: string;
  label: string;
  color: string;
}

/** Default color when none is provided */
const DEFAULT_COLOR = '#95A5A6';

/**
 * Hook to access note statuses from referential store
 */
export const useNoteStatuses = () => {
  const statusesData = useAppSelector(
    selectReferentialDataByKey(ReferentialKey.NoteStatus)
  );

  const statuses = useMemo<StatusItem[]>(() => {
    if (!statusesData) return [];

    return statusesData.map((ref) => ({
      id: ref.id || '',
      code: ref.code || '',
      label: ref.label || '',
      // Use color from referential (database), fallback to default gray
      color: ref.color || DEFAULT_COLOR
    }));
  }, [statusesData]);

  /**
   * Get status by ID
   */
  const getStatusById = useMemo(() => {
    return (id?: string): StatusItem | undefined => {
      if (!id) return undefined;
      return statuses.find((s) => s.id === id);
    };
  }, [statuses]);

  /**
   * Get status by code
   */
  const getStatusByCode = useMemo(() => {
    return (code?: string): StatusItem | undefined => {
      if (!code) return undefined;
      return statuses.find((s) => s.code === code);
    };
  }, [statuses]);

  /**
   * Get dropdown options for status filter (includes 'all' option)
   */
  const statusFilterOptions = useMemo(() => {
    return [
      { value: 'all', label: 'Tous' },
      ...statuses.map((s) => ({
        value: s.code,
        label: s.label
      }))
    ];
  }, [statuses]);

  /**
   * Get dropdown options for status selection (no 'all' option)
   */
  const statusSelectOptions = useMemo(() => {
    return statuses.map((s) => ({
      value: s.id,
      label: s.label,
      code: s.code,
      color: s.color
    }));
  }, [statuses]);

  return {
    statuses,
    getStatusById,
    getStatusByCode,
    statusFilterOptions,
    statusSelectOptions,
    isLoading: !statusesData
  };
};
